import axios from "axios";
import { getRawUrlBase, SiteResourcesEnum } from "shared/enums/resourceMappings";

interface PlanningValues {
  sites?: SiteValues[];
}

interface SiteValues {
  site_id?: number;
  plannings?: PlanningItemValues[];
}

interface PlanningItemValues {
  start_date?: string;
  end_date?: string;
  overidable?: boolean;
  mode?: string;
  fcr_engagement?: number | null;
  fcrn_engagement?: number | null;
  afrr_engagement_up?: number | null | string;
  afrr_engagement_down?: number | null | string;
  target_power_mw?: number | null;
  soc_restoration_power?: number | null;
  target_soc?: number | null;
}

interface PlanningErrors {
  sites?: SiteErrors[];
}

interface SiteErrors {
  plannings?: PlanningItemErrors[];
}

interface PlanningItemErrors {
  start_date?: string;
  end_date?: string;
  mode?: string;
  fcr_engagement?: string;
  fcrn_engagement?: string;
  afrr_engagement_up?: string;
  afrr_engagement_down?: string;
  target_power_mw?: string;
}

const getSitesInfos = async (siteIds: number[]) => {
  try {
    const url = `${getRawUrlBase(SiteResourcesEnum.Sites)}`;
    const { data } = await axios({
      method: "get",
      url,
      params: {
        id__in: siteIds.join(","),
        page: 1,
        size: 100,
      },
    });
    return data.items;
  } catch (error) {
    console.error("Error fetching site infos:", error);
    return [];
  }
};

const getSitesIds = (sites: SiteValues[]): number[] => {
  return sites.map((site) => site.site_id).filter((siteId): siteId is number => !!siteId);
};

export const validatePlanning = async (values: PlanningValues): Promise<PlanningErrors> => {
  const errors: PlanningErrors = {};

  if (values.sites) {
    const siteIds = getSitesIds(values.sites);
    const sitesInfos = siteIds.length ? await getSitesInfos(siteIds) : [];
    const siteErrorsArray = values.sites
      .map((site) => validateSite(site, sitesInfos))
      .filter((siteError): siteError is SiteErrors => siteError !== null);

    if (siteErrorsArray.length > 0) {
      errors.sites = siteErrorsArray;
    }
  }

  return errors;
};

const validateSite = (site: SiteValues, sitesInfos: any[]): SiteErrors | null => {
  const siteErrors: SiteErrors = {};

  if (site.plannings) {
    const planningErrorsArray = site.plannings
      .map((planning) => validatePlanningItem(planning, site.site_id, sitesInfos))
      .filter((planningError): planningError is PlanningItemErrors => planningError !== null);

    if (planningErrorsArray.length > 0) {
      siteErrors.plannings = planningErrorsArray;
    }
  }

  return Object.keys(siteErrors).length > 0 ? siteErrors : null;
};

const validatePlanningItem = (
  planning: PlanningItemValues,
  siteId: number | undefined,
  sitesInfos: any[],
): PlanningItemErrors | null => {
  const planningErrors: PlanningItemErrors = {};
  const targetedSite = sitesInfos.find((siteInfo: any) => siteInfo.id === siteId);

  validateDynamicFields(planning, targetedSite, planningErrors);
  validateMode(planning, planningErrors);
  validateDates(planning, planningErrors);

  return Object.keys(planningErrors).length > 0 ? planningErrors : null;
};

const validateDynamicFields = (planning: PlanningItemValues, targetedSite: any, planningErrors: PlanningItemErrors) => {
  const requiredFieldsMapping: Record<string, (keyof PlanningItemValues)[]> = {
    fcr_rte_v2: ["fcr_engagement"],
    fcr_rte_v2_no_reserve: ["fcr_engagement"],
    power: [],
    soc_management: ["target_soc"],
    afrr_rte: ["afrr_engagement_up", "afrr_engagement_down"],
    afrr_fcr_rte: ["afrr_engagement_up", "afrr_engagement_down", "fcr_engagement"],
    fingrid_fcrn: ["fcrn_engagement"],
  };

  const exemptedFields = ["target_power_mw"];

  const currentMode = planning.mode;
  const requiredFields = currentMode ? requiredFieldsMapping[currentMode] || [] : [];

  requiredFields.forEach((field) => {
    const fieldValue = planning[field];
    if (fieldValue === null || fieldValue === "") {
      planningErrors[field as keyof PlanningItemErrors] = "Required";
    } else if (typeof fieldValue === "number" && fieldValue < 0 && !exemptedFields.includes(field)) {
      planningErrors[field as keyof PlanningItemErrors] = "Must be at least 0";
    }
  });
};

const validateMode = (planning: PlanningItemValues, planningErrors: PlanningItemErrors) => {
  if (!planning.mode) {
    planningErrors.mode = "Mode is required";
  }
};

const validateDates = (planning: PlanningItemValues, planningErrors: PlanningItemErrors) => {
  const now = new Date();

  if (!planning.start_date) {
    planningErrors.start_date = "Start date is required";
  } else {
    const startDate = new Date(planning.start_date);
    if (startDate < now) {
      planningErrors.start_date = "Start date cannot be in the past";
    } else if (startDate.getMinutes() % 5 !== 0) {
      planningErrors.start_date = "Start date must be a multiple of 5 minutes";
    }
  }

  if (!planning.end_date) {
    planningErrors.end_date = "End date is required";
  } else {
    const endDate = new Date(planning.end_date);
    if (endDate < now) {
      planningErrors.end_date = "End date cannot be in the past";
    } else if (endDate.getMinutes() % 5 !== 0) {
      planningErrors.end_date = "End date must be a multiple of 5 minutes";
    }
  }

  if (planning.start_date && planning.end_date) {
    const startDate = new Date(planning.start_date);
    const endDate = new Date(planning.end_date);
    if (startDate >= endDate) {
      planningErrors.start_date = "Start date must be before end date";
      planningErrors.end_date = "End date must be after start date";
    }
  }
};
